/**
 * Session adoption of the **document root** (SCENE_ROOT_SPEC D8): giving the
 * open session its one authored root node, naming it, and keeping the tree to
 * that one root while the session edits.
 *
 * The predicates every host shares — which node is the document root, where
 * "the top" is, which roots may not be deleted or moved — live in
 * `trace-viewer/editor` (`documentRoot`, `topLevelParent`, `isEngineRoot`,
 * `isStructuralRoot`, `structuralRootRefusal`). This module is the editor's
 * half: {@link adoptDocumentRoot} is the single call an editor makes when a
 * session starts (the viewer never creates a root: a load does not, and
 * `clearScene()` deliberately does not).
 */

import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import { SCENE_ROOT_NAME } from 'trace-viewer/scene';
import { documentRoot, getUndoManager, RenameCommand, structuralRootRefusal } from 'trace-viewer/editor';

/**
 * The name a document root takes when the session has no title to give it.
 *
 * Deliberately NOT "Scene": the engine's mount point is called that
 * (`SCENE_ROOT_NAME`), and a round trip then reads `Scene → Scene → [content]`
 * in every tree. "Untitled" also says the truth — this document has not been
 * named yet — and {@link renameDefaultDocumentRoot} replaces it with the
 * asset's title the moment a save knows one.
 */
export const DEFAULT_DOCUMENT_ROOT_NAME = 'Untitled';

/**
 * The `Metadata` of a legacy (non-folder) publish: the document root's
 * behaviors as JSON, or undefined when the scene has no root. The same
 * snapshot the exported file is written from: live values, CMS-managed
 * behaviors left out.
 */
export function legacyRootMetadata(traceScene: TraceScene | null | undefined): string | undefined {
    const rootBehaviors = documentRoot(traceScene)?.serializeBehaviors({ excludeCmsManaged: true });
    return rootBehaviors ? JSON.stringify(rootBehaviors) : undefined;
}

/**
 * The name a newly adopted document root takes: what the session is called —
 * the asset/scene title, else the loaded scene's name, else `"Untitled"`.
 *
 * Pure, and deliberately tolerant of the empty string and whitespace: an
 * untitled CMS row arrives as `""` and must not name a node `""`.
 *
 * The engine mount point's own name (`SCENE_ROOT_NAME`, "Scene") is rejected as
 * a candidate: a legacy session whose scene is called that would otherwise
 * produce `Scene → Scene → [content]` in every tree and every placement.
 */
export function documentRootName(
    title?: string | null,
    sceneName?: string | null,
): string {
    const candidate = (value?: string | null): string => {
        const trimmed = value?.trim() ?? '';
        return trimmed === SCENE_ROOT_NAME ? '' : trimmed;
    };
    return candidate(title) || candidate(sceneName) || DEFAULT_DOCUMENT_ROOT_NAME;
}

/**
 * Whether the document root still carries the placeholder name — i.e. nothing
 * has ever named this document, neither the author nor the row it came from.
 */
export function hasPlaceholderDocumentRootName(
    traceScene: TraceScene | null | undefined,
): boolean {
    return documentRoot(traceScene)?.name === DEFAULT_DOCUMENT_ROOT_NAME;
}

/**
 * Give the document root the asset's title once a save knows one.
 *
 * A session that opened untitled (a brand-new scene, an untitled CMS row) named
 * its root {@link DEFAULT_DOCUMENT_ROOT_NAME}; by the time it is published the
 * row usually has a real title, and the file's own root should carry it — that
 * name is what every placement of this document shows in the tree
 * (`Cube (1) → Cube → [content]`, not `→ Untitled →`).
 *
 * Renames ONLY the placeholder: a root the author named, or one that came from
 * the opened document, is never rewritten by a save.
 *
 * This is a LIVE rename, pushed on the undo stack — unlike promotion's
 * synthesized group root (`SceneExporter.promoteToAsset`), which exists only in
 * the exported document and so has no live node to rename. Here the node IS
 * live and on screen, and renaming only the export would leave the tree and the
 * published file disagreeing about what the document is called.
 *
 * Call it BEFORE the publish takes its export snapshot, so the export, the tree
 * and the session's clean baseline all agree on the name.
 *
 * @returns true when the rename happened.
 */
export function renameDefaultDocumentRoot(
    traceScene: TraceScene | null | undefined,
    title: string | null | undefined,
): boolean {
    if (!traceScene) return false;
    const root = documentRoot(traceScene);
    if (!root || root.name !== DEFAULT_DOCUMENT_ROOT_NAME) return false;
    const next = title?.trim() ?? '';
    if (next.length === 0 || next === root.name || next === SCENE_ROOT_NAME) return false;

    const oldName = root.name;
    if (!traceScene.renameSceneObject(root, next)) return false;
    getUndoManager().push(new RenameCommand(root, oldName, root.name));
    console.log(
        `[DocumentRoot] Named the document root "${root.name}" from the asset title `
        + '(SCENE_ROOT_SPEC D8)',
    );
    return true;
}

/**
 * The stem of a file name — what a dropped file names the session it opens.
 *
 * Empty when the name is all extension (or absent), which lets
 * {@link documentRootName} fall through to the scene's own name.
 */
export function fileNameStem(fileName: string | null | undefined): string {
    const base = (fileName ?? '').split(/[\\/]/).pop() ?? '';
    return base.replace(/\.[^.]+$/, '').trim();
}

/** What {@link adoptDocumentRoot} did. */
export interface DocumentRootAdoption {
    /** The session's document root — existing or just created. */
    root: SceneObject;
    /**
     * True when the session had none and one was created or adopted (an empty
     * scene, a legacy flat `.zip`, a pre-D8 session). False when the opened
     * document already had its root, which is the D8 case and the common one.
     */
    adopted: boolean;
}

/**
 * Give this session a document root — the editor's "new scene / adopt" call.
 *
 * Called after every scene-establishing load (`viewer.open`, `openBlob`, the
 * CMS scene open, a new/cleared scene) and nowhere else. A D8 document already
 * has its root and this is a no-op that only records it as the session's; a
 * legacy or foreign session gets one created (empty scene) or its top-level
 * objects grouped under one, world-preserving.
 *
 * Callers take their dirty/snapshot baseline AFTER this so a freshly opened
 * document is never dirty just for having been adopted.
 */
export async function adoptDocumentRoot(
    traceScene: TraceScene | null | undefined,
    name?: string | null,
): Promise<DocumentRootAdoption | null> {
    if (!traceScene) return null;
    const existing = traceScene.getDocumentRoot();
    const root = await traceScene.ensureDocumentRoot(
        documentRootName(name, traceScene.sceneName),
    );
    const adopted = existing === null;
    if (adopted) {
        console.log(`[DocumentRoot] Adopted "${root.name}" as this session's document root (SCENE_ROOT_SPEC D8)`);
    }
    // After the adoption, so its own grouping is not caught by the rule it
    // installs (the new root's own parenting under the engine mount point).
    installDocumentRootPolicy(traceScene);
    return { root, adopted };
}

/** Scenes whose D8 invariant is already installed ({@link installDocumentRootPolicy}). */
const policyInstalled = new WeakSet<TraceScene>();

/**
 * Keep the session's tree to one root (D8), in the two ways a live edit can
 * break it:
 *
 * 1. **A reparent of a root is refused.** The document root IS the document and
 *    the engine root is not a node; neither moves. This is a `setReparentPolicy`
 *    veto, which the CMS scene's flat-scene policy later replaces with a
 *    stricter one that carries the same refusal.
 * 2. **A drop at the top lands UNDER the document root.** An Inspector drag onto
 *    the engine root would otherwise make a second top-level object — a document
 *    with two roots, which the writer would then have to re-wrap on save. The
 *    move is healed after the fact (world-preserving) rather than vetoed,
 *    because the veto also sees the gizmos' transient parent hops and refusing
 *    one of those strands the object.
 *
 * Installed once per scene, by {@link adoptDocumentRoot} and after it, so the
 * adoption's own grouping is not caught by its own rule.
 */
export function installDocumentRootPolicy(traceScene: TraceScene): void {
    if (policyInstalled.has(traceScene)) return;
    policyInstalled.add(traceScene);

    traceScene.setReparentPolicy((child) => {
        const refusal = structuralRootRefusal(child, 'move');
        if (refusal) {
            console.warn(`[DocumentRoot] ${refusal}`);
            return false;
        }
        return true;
    });

    traceScene.onObjectReparented((object) => {
        if (getUndoManager().isUndoRedoing) return;
        const root = documentRoot(traceScene);
        if (!root || object === root) return;
        if (object.getParent() !== traceScene.getSceneRoot()) return;
        console.log(`[DocumentRoot] "${object.name}" dropped at the top — parenting it under "${root.name}" (D8)`);
        // World-preserving, and it writes back only the pose: the heal changes
        // the parent and nothing else. Not reparentSceneObjectPreservingWorld,
        // which persists through refreshSceneData() and so rewrites the authored
        // behaviors from the live ones. An object created without a parent
        // lands here from inside createSceneObjectFromData, before reload() has
        // built any, and that rewrite left it with `behaviors: []`.
        object.transform.setParent(root.animationRoot ?? root.transform);
        object.captureTransform();
    });
}
